import { type MouseEvent, useCallback, useMemo } from "react";
import { type SearchResultRow, type SrpProductData } from "~/utils/models";
import { PredefinedTraits } from "~/features/productDetails/components/ProductTrait";
import { useSelector } from "react-redux";
import {
  isProductComparisonCheckboxVisible,
  ProductComparisonCheckbox,
} from "~/features/compareProducts/components/ProductComparisonCheckbox";
import { type RootState } from "~/app/store";
import { Card } from "~/features/configurator/Linear/components/Card";
import {
  AliasesParameter,
  OldDesignationParameter,
  SchaefflerIdParameter,
  SearchPageParamContainer,
} from "~/domains/product/components/ProductParams";
import { SearchTechValueTable } from "./components/SearchTechValueTable";
import useMediaQuery from "../../../../../../common/hooks/useMediaQuery";
import { fakeDescriptorFromProduct } from "~/domains/product/utils/productDescriptorUtils";
import { NewVersionMessage } from "../../../../../../common/components/Messages/NewVersionMessage";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { useTranslation } from "react-i18next";
import { SRPPackageRestrictionContext } from "~/features/PackageRestriction/SRPPackageRestrictionContext";
import { getGALocationForSearchItem } from "~/features/search/utils/searchPageUtils";
import { useSearchPageType } from "~/features/search/context/SearchPageTypeContext";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { canSeeAddToCartButton, canSeeWishlistButton } from "~/utils/features";
import {
  CardContent,
  ProductDetailsLayout,
} from "~/features/search/components/ProductResults/views/ListView/components/SearchResultItemCommonElements";
import { Product } from "~/domains/product/Product";
import classes from "~/features/search/components/ProductResults/views/ListView/SearchResultProductCustomer.module.less";
import { Box, Button, styled, Typography } from "~/common/mui";
import { canSeeBuyFromSalesPartnerButton } from "~/features/salesPartnerStock/utils/salesPartnerStockHelper";
import { useResultTileZoovuParams } from "~/features/search/utils/searchUtils";
import { getPurchaseDataFromUrl } from "~/features/productDetails/components/ProductSummary/PdpSummaryHelper";
import RichTooltip from "~/common/components/RichTooltip/RichTooltip";
import Cycle from "~/common/mui/icons/Cycle";
import parse from "html-react-parser";

import MMDAssistant from "~/features/productDetails/components/MMDAssistant/MMDAssistant";
import { URL_RECONDITIONABLE } from "~/utils/url/staticUrls";
import Tag from "~/common/components/Tag/Tag";
import { useSrpCmnLogic } from "~/features/productDetails/components/MMDAssistant/logic/useSrpCmnLogic";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import { usePackageRestrictionContext } from "~/features/PackageRestriction/PackageRestrictionContext";
import { renderButtonsNew } from "./components/useSrpButtons";
import { useAddProductToWishlistFlow } from "~/features/wishlist/hooks/useAddProductToWishlistFlow";

interface Props {
  isPdp?: boolean;
  resultRow: SearchResultRow;
  index: number;
  onProductClick: (area: string, product: SrpProductData) => void;
}

const SRPProductDetailsLayout = styled(ProductDetailsLayout)(({ theme }) => ({
  [".buttons-slot"]: {
    [theme.breakpoints.up("desktop-s")]: {
      alignSelf: "stretch",
      gridRow: "1 / 3",
    },
  },

  [".product-data-slot"]: {
    [theme.breakpoints.up("desktop-s")]: {
      gridColumn: "2 / 4",
    },
  },
}));

export const SearchResultProductNonCustomer = ({ resultRow, onProductClick, isPdp }: Props) => {
  const { t } = useTranslation();

  const pageType = useSearchPageType();
  const page = usePurchasePlace();

  const gaLocation = getGALocationForSearchItem(isPdp, pageType);

  const { productCode, features } = resultRow;

  const product = useSelector((state: RootState) => state.products.basic[productCode]) as SrpProductData;

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const { productSelectClick, productInformationClick } = useMemo(
    () => ({
      productInformationClick: () => onProductClick("Product information", product),
      productSelectClick: () => onProductClick("Select", product),
    }),
    [onProductClick, product],
  );

  const descriptor = useMemo(() => fakeDescriptorFromProduct(product), [product]);

  const showAddToCart = canSeeAddToCartButton(true);
  const showBuyFromFromSalesPartner = canSeeBuyFromSalesPartnerButton();
  const showAddToWishlist = !showBuyFromFromSalesPartner && canSeeWishlistButton();

  const { recommendationPlace, recommendationType } = getPurchaseDataFromUrl();
  const pdpLinkProps = {
    onClick: productInformationClick,
    reco_type: recommendationType,
    reco_place: recommendationPlace,
  };

  const mmdCmnData = useSrpCmnLogic(product);

  const restrictedPackage = usePackageRestrictionContext();
  const onClickCb = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      onProductClick("Select", product);
      await AddToCartService.openAddToCartWizard(
        {
          productCode: product.code,
          restrictionQuery: restrictedPackage ?? "",
          source: "srp",
        },
        { page, recommendationType, recommendationPlace },
      );
    },
    [onProductClick, page, recommendationPlace, recommendationType, product, restrictedPackage],
  );

  const [openWishlistClick, wishlistModal] = useAddProductToWishlistFlow({ productInput: product });

  const buttons = renderButtonsNew(
    descriptor,
    {
      atc: {
        disabled: window.SCHA.config.disableAllWS,
        onClick: onClickCb,
      },
      wishlist: {
        onClick: () => openWishlistClick(),
      },
    },
    { maxButtons: 2 },
  ).buttons;

  return (
    <>
      {wishlistModal}
      <SRPPackageRestrictionContext productCode={productCode}>
        <ProductDescriptorContext value={descriptor}>
          <Card
            className={"jsSearchResultProduct"}
            data-test-id={`search-result-item`}
            disableHover={true}
            {...useResultTileZoovuParams(product)}
          >
            <CardContent>
              <SRPProductDetailsLayout
                imageSlot={
                  <Product.PdpLink {...pdpLinkProps}>
                    <Product.PrimaryImageWithLogos className={classes.image} clickable={false} />
                  </Product.PdpLink>
                }
                productDataSlot={
                  <div className={classes.dataCol}>
                    <Product.PdpLink {...pdpLinkProps}>
                      <Product.FullDesignation clickable={false} className={classes.designation} />
                    </Product.PdpLink>
                    <Product.MMDChip />

                    <Product.PdpLink {...pdpLinkProps}>
                      <Product.ShortDescription clickable={false} />
                    </Product.PdpLink>

                    {product.reconditionable && (
                      <RichTooltip
                        content={
                          <Typography>
                            {parse(t("cmsnavigationnode.schaefflerContentCatalog.reconditionableInfo"))}
                          </Typography>
                        }
                        actions={[
                          <Button href={URL_RECONDITIONABLE} variant="text">
                            {t("cmsnavigationnode.schaefflerContentCatalog.moreInfo")}
                          </Button>,
                        ]}
                      >
                        <Tag icon={<Cycle fontSize="small" />} iconPosition="start" variant="success">
                          {t("cmsnavigationnode.schaefflerContentCatalog.reconditionable")}
                        </Tag>
                      </RichTooltip>
                    )}

                    <NewVersionMessage className={classes.newVersionMessage} baseProduct={product} />

                    <SearchPageParamContainer className={classes.paramContainer}>
                      <OldDesignationParameter variant={"search-page"} label={t("product.oldDesignations")} />
                      <SchaefflerIdParameter variant={"search-page"} />
                      <AliasesParameter variant={"search-page"} mode={"13-digits"} />
                    </SearchPageParamContainer>
                  </div>
                }
                techTableSlot={<SearchTechValueTable className={classes.technicalTable} features={features} />}
                actionSlot={null}
                priceDataSlot={null}
                buttonsSlot={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      alignItems: isDesktop ? "flex-end" : "flex-start",
                      height: "100%",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 2,
                      }}
                    >
                      <div>{isDesktop && product.preferred && <PredefinedTraits.PreferredProduct />}</div>
                      {isProductComparisonCheckboxVisible(product) && (
                        <ProductComparisonCheckbox productCode={productCode} gaLocation={gaLocation} />
                      )}
                    </Box>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, width: "100%" }}
                    >
                      {buttons}
                    </Box>
                  </Box>
                }
              />
            </CardContent>
            {product.displayMMDAssistant && (
              <MMDAssistant isPDP={false} marketDesignation={product.marketDesignation} initialData={mmdCmnData} />
            )}
          </Card>
        </ProductDescriptorContext>
      </SRPPackageRestrictionContext>
    </>
  );
};
